/**
 * plugins/alert.ts — Blocos de alerta (div.alert)
 *
 * Ferramentas: tipo (padrão / warn / green), cores custom de borda/fundo/texto.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, rgbToHex, closestInside } from '../types';

type AlertType = 'default' | 'alert-warn' | 'alert-green';

export class AlertPlugin implements VtbPlugin {
    readonly panelId = 'vtb-alert';
    readonly selectorHints = ['div.alert', '.alert', '[data-vtb-id="alert"]'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            e.classList.contains('alert') || e.dataset.vtbId === 'alert'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Alerta', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;

        // Determina tipo atual
        let currentType: AlertType = 'default';
        if (el.classList.contains('alert-warn'))  currentType = 'alert-warn';
        if (el.classList.contains('alert-green')) currentType = 'alert-green';

        const sel = registry.q<HTMLSelectElement>('vtb-alert-type');
        if (sel) sel.value = currentType;

        // Cores atuais - tenta pegar computado se estilo inline estiver vazio
        const style = window.getComputedStyle(el);
        const bgVal = el.style.backgroundColor || style.backgroundColor;
        const bdVal = el.style.borderLeftColor || style.borderLeftColor;
        const fgVal = el.style.color || style.color;

        const bg = registry.q<HTMLInputElement>('vtb-alert-bg');
        const bd = registry.q<HTMLInputElement>('vtb-alert-border');
        const fg = registry.q<HTMLInputElement>('vtb-alert-fg');
        
        if (bg) bg.value = rgbToHex(bgVal) || '#fff9c4';
        if (bd) bd.value = rgbToHex(bdVal) || '#f9a825';
        if (fg) fg.value = rgbToHex(fgVal) || '#333333';
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-alert-apply')?.addEventListener('click', () => this._apply(registry));
        registry.q<HTMLInputElement>('vtb-alert-bg')
            ?.addEventListener('input', () => this._livePreview(registry));
        registry.q<HTMLInputElement>('vtb-alert-border')
            ?.addEventListener('input', () => this._livePreview(registry));
        registry.q<HTMLInputElement>('vtb-alert-fg')
            ?.addEventListener('input', () => this._livePreview(registry));
        registry.q<HTMLSelectElement>('vtb-alert-type')
            ?.addEventListener('change', () => this._applyType(registry));
    }

    private _applyType(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el  = ctx.el;
        const sel = registry.q<HTMLSelectElement>('vtb-alert-type')?.value as AlertType;
        el.classList.remove('alert-warn', 'alert-green');
        if (sel && sel !== 'default') el.classList.add(sel);
        // Limpa estilos inline ao selecionar preset
        el.style.removeProperty('background-color');
        el.style.removeProperty('border-left-color');
        el.style.removeProperty('color');
        registry.sync();
    }

    private _livePreview(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el = ctx.el;
        const bg = registry.q<HTMLInputElement>('vtb-alert-bg')?.value;
        const bd = registry.q<HTMLInputElement>('vtb-alert-border')?.value;
        const fg = registry.q<HTMLInputElement>('vtb-alert-fg')?.value;
        if (bg) el.style.backgroundColor = bg;
        if (bd) el.style.borderLeftColor  = bd;
        if (fg) el.style.color = fg;
        registry.sync();
    }

    private _apply(registry: IVtbRegistry): void {
        this._applyType(registry);
        this._livePreview(registry);
    }
}
